import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { chatQueryKeys } from 'src/api/queryKeys'
import { getUserConnectionsByStatus, ConnectionDetailType } from 'src/api/connect'
import { getPendingEvents, Event } from 'src/api/event'
import { useAppDispatch } from 'src/store/hooks'
import { updatePendingRequests } from 'src/store/slices/requests'

interface UsePendingRequestsOptions {
  enablePolling?: boolean
  pollingInterval?: number
}

interface PendingRequestsData {
  connections: ConnectionDetailType[]
  events: Event[]
}

/**
 * Global hook for fetching pending connection and event requests using TanStack Query
 *
 * Features:
 * - Automatic polling (configurable interval, default 30 seconds)
 * - Automatic refetching on window focus
 * - Automatic refetching on reconnect
 * - Syncs data to Redux for badge count display
 * - Shared cache across all components
 *
 * This hook should be called once at the app level (e.g., in PrivateLayout)
 * to enable global polling and automatic badge updates.
 *
 * @param options - Configuration options
 * @param options.enablePolling - Whether to enable automatic polling (default: false)
 * @param options.pollingInterval - Polling interval in milliseconds (default: 30000)
 * @returns Object containing pending connections, events, loading state, and refetch function
 */
export const usePendingRequests = (options?: UsePendingRequestsOptions) => {
  const dispatch = useAppDispatch()
  const { enablePolling = false, pollingInterval = 30000 } = options ?? {}

  const query = useQuery<PendingRequestsData>({
    queryKey: chatQueryKeys.pendingRequests(),
    queryFn: async () => {
      const [connections, events] = await Promise.all([
        getUserConnectionsByStatus('receiver', 'pending') as Promise<ConnectionDetailType[]>,
        getPendingEvents(),
      ])

      // Ensure we always return arrays
      return {
        connections: Array.isArray(connections) ? connections : [],
        events: Array.isArray(events) ? events : [],
      }
    },

    // Polling configuration
    refetchInterval: enablePolling ? pollingInterval : false,
    refetchIntervalInBackground: false, // Only poll when tab is active

    // Automatic refetch on window focus and reconnect
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,

    // Consider data stale after 20 seconds
    staleTime: 20 * 1000,

    // Keep in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
  })

  // Sync to Redux whenever data changes (replaces deprecated onSuccess)
  useEffect(() => {
    if (query.data) {
      dispatch(
        updatePendingRequests({
          connections: query.data.connections,
          events: query.data.events,
        })
      )
    }
  }, [query.data, dispatch])

  // Refetch immediately on system-inbox push (no waiting for next poll tick)
  useEffect(() => {
    const handler = () => void query.refetch()
    window.addEventListener('connectup:system-message', handler)
    return () => window.removeEventListener('connectup:system-message', handler)
  }, [query])

  return {
    // Pending connections list
    pendingConnections: query.data?.connections ?? [],
    // Pending events list
    pendingEvents: query.data?.events ?? [],
    // Total pending count (for badge)
    pendingCount: (query.data?.connections.length ?? 0) + (query.data?.events.length ?? 0),
    // Loading state (initial load)
    isLoading: query.isLoading,
    // Error state
    error: query.error,
    // Whether the query is currently fetching (including background refetch)
    isFetching: query.isFetching,
    // Refetch function for manual refresh
    refetch: query.refetch,
  }
}
